/**
 * Configuration utilities for file upload handling
 * Provides centralized access to configurable file size limits
 */

import config from '../config';
import { formatFileSize } from './formatUtils';

/**
 * Get the maximum file size in bytes from configuration
 */
export const getMaxFileSize = (): number => {
  return config.features.maxFileSize;
};

/**
 * Get the maximum file size as a formatted human-readable string
 */
export const getMaxFileSizeFormatted = (): string => {
  return formatFileSize(config.features.maxFileSize);
};

/**
 * Check if a file size is within the configured limit
 */
export const isFileSizeValid = (fileSize: number): boolean => {
  return fileSize <= config.features.maxFileSize;
};

/**
 * Get file size error message for files that exceed the limit
 */
export const getFileSizeErrorMessage = (fileSize: number): string => {
  return `File too large (${formatFileSize(fileSize)}). Maximum size is ${getMaxFileSizeFormatted()}.`;
};

/**
 * Get the large file threshold in bytes (when to use chunked upload)
 * Currently set to 50MB as a reasonable default
 */
export const getLargeFileThreshold = (): number => {
  return 50 * 1024 * 1024; // 50MB
};

/**
 * Determine if a file should use chunked upload based on size
 */
export const shouldUseChunkedUpload = (fileSize: number): boolean => {
  return fileSize > getLargeFileThreshold();
};

/**
 * Configuration validation - ensures setup is correct
 */
export const validateConfiguration = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  const maxSize = getMaxFileSize();
  
  // Validate max file size is reasonable
  if (maxSize <= 0) {
    errors.push('Maximum file size must be greater than 0');
  }

  if (maxSize > 2 * 1024 * 1024 * 1024) { // 2GB
    errors.push('Maximum file size exceeds reasonable limit (2GB)');
  }
  
  // Validate chunked threshold makes sense
  if (getLargeFileThreshold() >= maxSize) {
    errors.push('Large file threshold should be less than maximum file size');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get configuration summary for debugging
 */
export const getConfigurationSummary = () => {
  const validation = validateConfiguration();
  
  return {
    maxFileSize: {
      bytes: getMaxFileSize(),
      formatted: getMaxFileSizeFormatted()
    },
    largeFileThreshold: {
      bytes: getLargeFileThreshold(),
      formatted: formatFileSize(getLargeFileThreshold())
    },
    chunkedUploadEnabled: true,
    validation,
    environment: {
      REACT_APP_DRAWS_MAX_FILE_SIZE: process.env.REACT_APP_DRAWS_MAX_FILE_SIZE || 'not set'
    }
  };
};